import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import {
  UserPlus,
  Users,
  UserCheck,
  ChevronDown,
  Phone,
  RotateCcw,
  Check,
  MapPin,
  Activity,
  Building,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Upload,
  CreditCard,
  FileText,
  XCircle,
  ClipboardList,
  Stethoscope,
  ShieldCheck,
  Eye,
  CalendarClock,
  Calendar,
  Sparkles,
  Search,
  Lock,
  Smartphone,
  Clock
} from 'lucide-react';
import SearchableSelect from '../../Components/common/SearchableSelect';

// Helper to calculate exact age from Date of Birth
const calculateAgeFromDob = (dobString) => {
  if (!dobString) return '';
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 0 ? Math.min(125, age) : 0;
};

const LeadCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [intakeCategory, setIntakeCategory] = useState('appointment'); // 'appointment' or 'surgical'
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // 🔍 Phone Lookup & Existing Patient History State
  const [isSearchingPhone, setIsSearchingPhone] = useState(false);
  const [lookupData, setLookupData] = useState(null);
  const [selectedPatientProfile, setSelectedPatientProfile] = useState(null);
  const [isNewPatientUnderSamePhone, setIsNewPatientUnderSamePhone] = useState(false);
  const [isPhoneConfirmed, setIsPhoneConfirmed] = useState(false);
  const [patientFilterQuery, setPatientFilterQuery] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(true);

  // Refs for mandatory fields (auto-scroll + highlight)
  const patientNameRef = useRef(null);
  const patientPhoneRef = useRef(null);
  const patientAgeRef = useRef(null);
  const patientAddressRef = useRef(null);
  const treatmentRequiredRef = useRef(null);
  const chiefComplaintsRef = useRef(null);
  const eligibilityModeRef = useRef(null);

  const [formData, setFormData] = useState({
    // Patient Details
    patientName: '',
    patientPhone: '',
    patientEmail: '',
    patientAge: '',
    patientGender: 'male',
    bloodGroup: '',
    dateOfBirth: '',
    emergencyContact: '',
    abhaId: '',
    patientAddress: '',
    treatmentRequired: '',
    chiefComplaints: '',
    pastMedicalHistory: '',
    comorbidities: '',

    isManualPastEntry: false,
    pastPaymentProof: null,

    // Role based setup
    leadType: '', // 'lead_only', 'surgery'
    assignedHospital: '', // Selected if doctor, auto if hospital

    // Eligibility
    eligibilityMode: '', // 'ayushman', 'cash', 'insurance'
    ayushmanCardImage: null,
    patientIdProof: null,

    // Documents
    medicalReports: [],

    notes: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  // 📞 Auto-populate from URL query params (e.g. from Exotel Incoming Call screen pop)
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    const nameParam = searchParams.get('name');
    if (phoneParam || nameParam) {
      const cleanPhone = phoneParam ? String(phoneParam).replace(/\D/g, '').slice(-10) : '';
      setFormData(prev => ({
        ...prev,
        ...(cleanPhone ? { patientPhone: cleanPhone } : {}),
        ...(nameParam && nameParam !== 'Unknown Caller' ? { patientName: nameParam } : {})
      }));
    }
  }, [searchParams]);

  const fetchInitialData = async () => {
    try {
      // 1. Fetch User Profile to get Partner ID
      const profileRes = await api.get('/auth/profile');
      const profile = profileRes.data.data;

      let pProfile = null;
      if (user?.role === 'doctor') pProfile = profile.doctorProfile;
      else if (user?.role === 'hospital') pProfile = profile.hospitalProfile;
      else if (user?.role === 'marketing_agent') pProfile = profile.marketingProfile;

      setPartnerProfile(pProfile);

      // Default lead type for marketing
      if (user?.role === 'marketing_agent') {
        setFormData(prev => ({ ...prev, leadType: 'lead_only' }));
      }

      // Auto-assign hospital for Hospitals
      if (user?.role === 'hospital' && pProfile) {
        setFormData(prev => ({ ...prev, assignedHospital: pProfile._id }));
      }

      // 2. Fetch In-Panel Hospitals for Doctors
      if (user?.role === 'doctor' || user?.role === 'admin') {
        const hospRes = await api.get('/hospitals?limit=500');
        setHospitals(hospRes.data.data.hospitals || []);
      }
    } catch (error) {
      console.error("Initialization Failed:", error);
      toast.error("Failed to load environment data");
    }
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'patientPhone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    if (name === 'abhaId') {
      value = value.replace(/\D/g, '').slice(0, 14);
    }
    // Prevent patient age from going below 0 or above 125, while allowing clearing
    if (name === 'patientAge') {
      if (value === '') {
        value = '';
      } else {
        const clean = value.toString().replace(/[^0-9]/g, '');
        value = clean === '' ? '' : Math.min(125, Math.max(0, parseInt(clean, 10)));
      }
    }
    // Auto-calculate age when Date of Birth is selected
    if (name === 'dateOfBirth') {
      const calcAge = calculateAgeFromDob(value);
      setFormData(prev => ({
        ...prev,
        dateOfBirth: value,
        ...(calcAge !== '' ? { patientAge: calcAge } : {})
      }));
      if (calcAge !== '' && fieldErrors.patientAge) {
        setFieldErrors(prev => ({ ...prev, patientAge: false }));
      }
      return;
    }
    // Clear error highlight when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: false }));
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 🔍 Phone Lookup Execution (Called on 10 digits or clicking CONTINUE)
  const executePhoneLookup = async (phoneToLookup) => {
    const clean = String(phoneToLookup || formData.patientPhone || '').replace(/\D/g, '').slice(-10);
    if (clean.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsSearchingPhone(true);
    try {
      const res = await api.get(`/leads/lookup-phone/${clean}`);
      const data = res.data?.data;
      setLookupData(data);

      if (data?.patients?.length > 0) {
        // Patients found: Keep Card 2 locked until user selects an existing patient or clicks "Register New Patient"
        setIsPhoneConfirmed(false);
        setShowPatientDropdown(true);
      } else {
        // Brand new number: Automatically unlock Card 2 for registration!
        setIsPhoneConfirmed(true);
        setSelectedPatientProfile(null);
        setIsNewPatientUnderSamePhone(true);
        setShowPatientDropdown(false);
      }
    } catch (err) {
      console.error("Phone lookup failed:", err);
      setIsPhoneConfirmed(true);
    } finally {
      setIsSearchingPhone(false);
    }
  };

  // 🔍 Debounced Auto-Lookup when 10-digit mobile number is entered
  useEffect(() => {
    const clean = String(formData.patientPhone || '').replace(/\D/g, '').slice(-10);
    if (clean.length === 10) {
      const timer = setTimeout(() => {
        executePhoneLookup(clean);
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setLookupData(null);
      setSelectedPatientProfile(null);
      setIsNewPatientUnderSamePhone(false);
      setIsPhoneConfirmed(false);
      setShowPatientDropdown(true);
      setPatientFilterQuery('');
    }
  }, [formData.patientPhone]);

  const handleSelectPatient = (patient, notify = true) => {
    setSelectedPatientProfile(patient);
    setIsNewPatientUnderSamePhone(false);
    setIsPhoneConfirmed(true);
    setShowPatientDropdown(false);

    setFormData(prev => ({
      ...prev,
      patientName: patient.patientName || prev.patientName,
      patientEmail: patient.patientEmail || prev.patientEmail,
      patientAge: (patient.patientAge !== undefined && patient.patientAge !== null && patient.patientAge !== '') ? String(patient.patientAge) : prev.patientAge,
      patientGender: patient.patientGender || prev.patientGender || 'male',
      bloodGroup: patient.bloodGroup || prev.bloodGroup,
      dateOfBirth: patient.dateOfBirth || prev.dateOfBirth,
      emergencyContact: patient.emergencyContact || prev.emergencyContact,
      abhaId: patient.abhaId || prev.abhaId,
      patientAddress: patient.patientAddress || prev.patientAddress,
      treatmentRequired: prev.treatmentRequired || patient.treatmentRequired || (intakeCategory === 'appointment' ? 'Doctor Consultation' : ''),
      chiefComplaints: prev.chiefComplaints || patient.chiefComplaints || '',
      pastMedicalHistory: patient.pastMedicalHistory || prev.pastMedicalHistory,
      comorbidities: patient.comorbidities || prev.comorbidities,
      eligibilityMode: patient.coverageMode || (patient.hasAyushmanCard ? 'ayushman' : prev.eligibilityMode)
    }));

    // Clear field errors for auto-filled fields
    setFieldErrors(prev => ({
      ...prev,
      patientName: false,
      patientPhone: false,
      patientAge: false,
      patientAddress: false
    }));

    if (notify) {
      toast.success(`Selected ${patient.patientName} — Previous records loaded!`, { icon: '👤' });
    }
  };

  const handleAddNewPatientUnderSamePhone = () => {
    setSelectedPatientProfile(null);
    setIsNewPatientUnderSamePhone(true);
    setIsPhoneConfirmed(true);
    setShowPatientDropdown(false);

    setFormData(prev => ({
      ...prev,
      patientName: '',
      patientEmail: '',
      patientAge: '',
      patientGender: 'male',
      bloodGroup: '',
      dateOfBirth: '',
      emergencyContact: '',
      abhaId: '',
      patientAddress: '',
      treatmentRequired: intakeCategory === 'appointment' ? 'Doctor Consultation' : '',
      chiefComplaints: '',
      pastMedicalHistory: '',
      comorbidities: '',
      eligibilityMode: ''
    }));

    toast(`Ready to register new patient under ${formData.patientPhone}`, { icon: '➕' });
    if (patientNameRef.current) {
      patientNameRef.current.focus();
    }
  };

  const handleResetPhoneLookup = () => {
    setIsPhoneConfirmed(false);
    setSelectedPatientProfile(null);
    setIsNewPatientUnderSamePhone(false);
    setShowPatientDropdown(true);
    if (patientPhoneRef.current) {
      patientPhoneRef.current.focus();
    }
  };

  const handleFileUpload = async (files, type) => {
    if (!files || files.length === 0) return;

    // Check file size (50MB limit)
    const MAX_SIZE = 50 * 1024 * 1024;
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`File "${file.name}" exceeds 50MB limit. Please upload smaller files.`);
        return;
      }
    }

    if (type === 'medical' && (formData.medicalReports.length + files.length > 15)) {
      toast.error('Limit: 15 reports');
      return;
    }

    const fileFormData = new FormData();
    files.forEach(f => fileFormData.append('documents', f));

    try {
      setLoading(true);
      const response = await api.post('/upload', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedFiles = response.data.data.files;
      if (type === 'ayushman') {
        setFormData(prev => ({ ...prev, ayushmanCardImage: uploadedFiles[0].url }));
        toast.success("Ayushman Card Attached");
      } else if (type === 'id_proof') {
        setFormData(prev => ({ ...prev, patientIdProof: uploadedFiles[0].url }));
        toast.success("Identity Proof Attached");
      } else if (type === 'past_proof') {
        setFormData(prev => ({ ...prev, pastPaymentProof: { url: uploadedFiles[0].url, name: uploadedFiles[0].filename } }));
        toast.success("Payment Proof Attached");
      } else {
        const newReports = uploadedFiles.map(f => ({ url: f.url, name: f.filename }));
        setFormData(prev => ({
          ...prev,
          medicalReports: [...prev.medicalReports, ...newReports]
        }));
        toast.success(`${uploadedFiles.length} Reports Added`);
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const scrollToRef = (ref) => {
    ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => ref?.current?.focus(), 400);
  };

  const handleAppointmentSubmit = async () => {
    const errors = {};
    if (!formData.patientName) errors.patientName = true;
    if (!formData.patientPhone) errors.patientPhone = true;
    if (!formData.patientAge) errors.patientAge = true;
    if (!formData.patientAddress) errors.patientAddress = true;
    if (!formData.chiefComplaints && !formData.treatmentRequired) errors.chiefComplaints = true;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.patientName) scrollToRef(patientNameRef);
      else if (errors.patientPhone) scrollToRef(patientPhoneRef);
      else if (errors.patientAge) scrollToRef(patientAgeRef);
      else if (errors.patientAddress) scrollToRef(patientAddressRef);
      else if (errors.chiefComplaints) scrollToRef(chiefComplaintsRef);
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.patientPhone.length !== 10) {
      setFieldErrors({ patientPhone: true });
      scrollToRef(patientPhoneRef);
      toast.error('Valid 10-digit mobile required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patientName: formData.patientName,
        patientPhone: formData.patientPhone.replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10),
        patientEmail: formData.patientEmail || null,
        patientAge: parseInt(formData.patientAge, 10) || null,
        patientGender: formData.patientGender || 'male',
        bloodGroup: formData.bloodGroup || null,
        dateOfBirth: formData.dateOfBirth || null,
        emergencyContact: formData.emergencyContact || null,
        patientAddress: formData.patientAddress,
        city: formData.patientAddress,
        treatmentRequired: formData.treatmentRequired || formData.chiefComplaints || 'Doctor Consultation',
        disease: formData.chiefComplaints || formData.treatmentRequired || 'Doctor Consultation',
        chiefComplaints: formData.chiefComplaints || null,
        pastMedicalHistory: formData.pastMedicalHistory || null,
        comorbidities: formData.comorbidities || null,
        leadType: 'appointment',
        pipelineChoice: 'appointment',
        source: user?.role === 'marketing_agent' ? 'marketing' : (user?.role || 'CRM'),
        leadGeneratedByType: user?.role === 'marketing_agent' ? 'marketing' : (user?.role || 'CRM'),
        leadGeneratedById: partnerProfile?._id || null,
        leadGeneratedByName: user?.fullName || null,
        createdBy: user?._id
      };

      const response = await api.post('/leads', payload);
      toast.success("Doctor Appointment Lead Generated Successfully!");
      const createdLead = response.data.data;

      // Obtain associated appointment ID
      let apptId = createdLead?.associatedAppointment?._id || createdLead?.associatedAppointment;

      if (!apptId && createdLead?._id) {
        try {
          const apptRes = await api.get(`/appointments?leadId=${createdLead._id}&limit=1`);
          const appts = apptRes.data?.data?.appointments || apptRes.data?.data || [];
          if (Array.isArray(appts) && appts.length > 0) {
            apptId = appts[0]._id;
          }
        } catch (e) {
          console.warn("Could not fetch newly created appointment by leadId", e);
        }
      }

      if (apptId) {
        navigate(`/appointments/${apptId}?leadId=${createdLead?._id}&from=leads&origin=leads&tab=doctor_type&menu=appointments`);
      } else {
        navigate(`/appointments`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Internal validation failed");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (intakeCategory === 'appointment') {
      return handleAppointmentSubmit();
    }
    // Validation for Step 1
    if (step === 1) {
      const errors = {};
      if (!formData.patientName) errors.patientName = true;
      if (!formData.patientPhone) errors.patientPhone = true;
      if (!formData.patientAge) errors.patientAge = true;
      if (!formData.patientAddress) errors.patientAddress = true;
      if (!formData.treatmentRequired) errors.treatmentRequired = true;

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        // Scroll to first empty required field in order
        if (errors.patientName) { scrollToRef(patientNameRef); }
        else if (errors.patientPhone) { scrollToRef(patientPhoneRef); }
        else if (errors.patientAge) { scrollToRef(patientAgeRef); }
        else if (errors.patientAddress) { scrollToRef(patientAddressRef); }
        else if (errors.treatmentRequired) { scrollToRef(treatmentRequiredRef); }
        toast.error('Please fill all required fields');
        return;
      }
      if (formData.patientPhone.length !== 10) {
        setFieldErrors({ patientPhone: true });
        scrollToRef(patientPhoneRef);
        toast.error('Valid 10-digit mobile required');
        return;
      }
    }
    // Validation for Step 2
    if (step === 2) {
      if (user?.role === 'doctor' && !formData.assignedHospital) {
        toast.error('Please select an Operating Facility');
        return;
      }
      if (user?.role !== 'marketing_agent' && !formData.leadType) {
        toast.error('Please select Lead/Surgery intent');
        return;
      }
    }
    const isDoctorOrHospital = user?.role === 'doctor' || user?.role === 'hospital';
    if (step === 1 && !isDoctorOrHospital) {
      setStep(3);
    } else {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    if (intakeCategory === 'appointment') {
      return handleAppointmentSubmit();
    }
    setLoading(true);
    try {
      const payload = {
        patientName: formData.patientName,
        patientPhone: formData.patientPhone.replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10),
        patientEmail: formData.patientEmail || null,
        patientAge: parseInt(formData.patientAge, 10) || null,
        patientGender: formData.patientGender,
        bloodGroup: formData.bloodGroup || null,
        dateOfBirth: formData.dateOfBirth || null,
        emergencyContact: formData.emergencyContact || null,
        abhaId: formData.abhaId || null,
        patientAddress: formData.patientAddress,
        city: formData.patientAddress, // Map to city for search compatibility
        treatmentRequired: formData.treatmentRequired,
        chiefComplaints: formData.chiefComplaints || null,
        pastMedicalHistory: formData.pastMedicalHistory || null,
        comorbidities: formData.comorbidities || null,
        isManualPastEntry: formData.isManualPastEntry || false,

        // Ownership Logic
        leadGeneratedByType: formData.isManualPastEntry ? 'past_surgery' : (user?.role === 'marketing_agent' ? 'marketing' : user?.role),
        leadGeneratedById: partnerProfile?._id || null,
        leadGeneratedByName: user?.fullName || null,
        leadType: formData.leadType || 'lead_only',
        source: formData.isManualPastEntry ? 'past_surgery' : (user?.role === 'marketing_agent' ? 'marketing' : user?.role),

        // Assignments
        assignedHospital: formData.assignedHospital || null,

        // Eligibility
        hasAyushmanCard: formData.eligibilityMode === 'ayushman',
        paymentType: formData.eligibilityMode === 'cash' ? ['Cash'] : formData.eligibilityMode === 'insurance' ? ['Insurance'] : [],

        // Attachments
        documents: [
          ...(formData.ayushmanCardImage ? [{ name: 'Ayushman Card', type: 'ayushman_card', url: formData.ayushmanCardImage }] : []),
          ...(formData.patientIdProof ? [{ name: 'ID Proof', type: 'id_proof', url: formData.patientIdProof }] : []),
          ...(formData.pastPaymentProof ? [{ name: 'Payment Settlement Proof', type: 'past_payment_proof', url: formData.pastPaymentProof.url }] : []),
          ...formData.medicalReports.map(r => ({ name: r.name, type: 'medical_report', url: r.url }))
        ],

        // Clinical Flags for existing system compatibility
        isDoctorPerformingSurgery: (user?.role === 'doctor' && formData.leadType === 'surgery') ? 'Yes' : 'No',
        isDoctorOnlyProvidingLead: (user?.role === 'doctor' && formData.leadType === 'lead_only') ? 'Yes' : 'No',
        isSurgeryPerformedInTheirHospital: (user?.role === 'hospital' && formData.leadType === 'surgery') ? 'Yes' : 'No',
        isHospitalOnlyProvidingLead: (user?.role === 'hospital' && formData.leadType === 'lead_only') ? 'Yes' : 'No',

        createdBy: user?._id
      };

      const response = await api.post('/leads', payload);
      toast.success("Lead Generated Successfully!");
      const createdLead = response.data.data;
      const surgId = createdLead?.associatedSurgery?._id || createdLead?._id;
      navigate(`/surgeries/${surgId}?leadId=${createdLead?._id}&from=leads&origin=leads&tab=pipeline&menu=leads`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Internal validation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 flex flex-col items-center">
      {/* 🚀 HEADER & PROGRESS */}
      <div className="w-full max-w-4xl mb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-100 mb-6">
          <ClipboardList className="w-4 h-4 text-[#143E78]" />
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Healthcare Deal Flow</span>
        </div>
        <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight mb-3 px-2">Patient Lead Registration</h1>
        <p className="text-slate-500 font-bold max-w-md mx-auto px-4 text-xs sm:text-base">
          {!isPhoneConfirmed
            ? 'Search for a patient by mobile number to get started.'
            : intakeCategory === 'appointment'
              ? 'Quick intake for doctor consultation & outpatient appointments.'
              : 'Enter patient details to register a surgery workflow lead.'}
        </p>

        {/* 🩺 Intake Type Selector — only shown after patient is confirmed */}
        {isPhoneConfirmed && (
          <>
            <div className="flex justify-center mt-6">
              <div className="inline-flex p-1.5 bg-white rounded-2xl shadow-sm border border-slate-200 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIntakeCategory('appointment');
                    setFormData(prev => ({
                      ...prev,
                      leadType: 'appointment',
                      treatmentRequired: prev.treatmentRequired || 'Doctor Consultation'
                    }));
                    setStep(1);
                    setFieldErrors({});
                  }}
                  className={`py-2.5 px-6 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2.5 cursor-pointer ${intakeCategory === 'appointment'
                      ? 'bg-[#143E78] text-white shadow-md shadow-blue-950/20'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                >
                  <CalendarClock className="w-4 h-4 shrink-0" />
                  <span>Doctor Appointment</span>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${intakeCategory === 'appointment' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    Minimal
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIntakeCategory('surgical');
                    setFormData(prev => ({
                      ...prev,
                      leadType: prev.leadType === 'appointment' ? '' : prev.leadType,
                      treatmentRequired: prev.treatmentRequired === 'Doctor Consultation' ? '' : prev.treatmentRequired
                    }));
                    setFieldErrors({});
                  }}
                  className={`py-2.5 px-6 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2.5 cursor-pointer ${intakeCategory === 'surgical'
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                >
                  <Activity className="w-4 h-4 shrink-0" />
                  <span>Surgical Care</span>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${intakeCategory === 'surgical' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    Full Surgery
                  </span>
                </button>
              </div>
            </div>

            {/* Stepper Grid or Minimal Badge */}
            {intakeCategory === 'appointment' ? (
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-[#143E78] border border-blue-200/80 rounded-full text-xs font-black uppercase tracking-wider mt-6 animate-fadeIn">
                <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                <span>1-Step Fast Appointment Intake</span>
              </div>
            ) : (
              <div className="flex justify-center gap-4 mt-8 animate-fadeIn">
                {((user?.role === 'doctor' || user?.role === 'hospital') ? [1, 2, 3] : [1, 2]).map((s) => {
                  const activeStepNum = (user?.role === 'doctor' || user?.role === 'hospital') ? step : (step === 3 ? 2 : 1);
                  return (
                    <div key={s} className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black transition-all ${activeStepNum === s ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100' :
                        activeStepNum > s ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-50' : 'bg-slate-100 text-slate-400'
                        }`}>
                        {activeStepNum > s ? <CheckCircle className="w-5 h-5" /> : s}
                      </div>
                      {s < ((user?.role === 'doctor' || user?.role === 'hospital') ? 3 : 2) && <div className={`w-12 h-1 rounded-full ${activeStepNum > s ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* 🧩 WIZARD CONTENT */}
      <div className="w-full max-w-4xl space-y-6">

        {/* 🔍 CARD 1: PATIENT LOOKUP (TOP CARD — SHOWN IN STEP 1) */}
        {step === 1 && (
          <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8 animate-fadeIn">
              {/* Header */}
              <div className="flex items-center gap-3.5 mb-5">
                <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-black shrink-0">
                  <Search size={18} />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-slate-900 leading-tight">Patient Lookup</h3>
                  <p className="text-xs text-slate-500 font-medium">Search by mobile number to begin.</p>
                </div>
              </div>

              {/* Mobile Access Number Input & Continue Button */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">
                  Mobile Access Number <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col sm:flex-row items-stretch gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400 select-none pointer-events-none">
                      +91
                    </span>
                    <input
                      ref={patientPhoneRef}
                      type="tel"
                      name="patientPhone"
                      value={formData.patientPhone}
                      onChange={handleInputChange}
                      placeholder="Enter 10-digit number"
                      maxLength={10}
                      className={`w-full pl-14 pr-11 py-4 bg-slate-50 border-2 rounded-2xl outline-none font-mono font-bold text-slate-900 transition-all ${
                        fieldErrors.patientPhone
                          ? 'border-red-400 bg-red-50 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600 focus:bg-white'
                      }`}
                    />
                    {lookupData && isPhoneConfirmed ? (
                      <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-500 w-5 h-5 pointer-events-none" />
                    ) : (
                      <Smartphone className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => executePhoneLookup(formData.patientPhone)}
                    disabled={isSearchingPhone || !formData.patientPhone || formData.patientPhone.length < 10}
                    className="px-7 py-4 bg-[#0E355B] hover:bg-[#08233D] disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-xs uppercase tracking-widest rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0 shadow-md shadow-slate-900/10"
                  >
                    {isSearchingPhone ? (
                      <>
                        <RotateCcw size={14} className="animate-spin" />
                        <span>SEARCHING...</span>
                      </>
                    ) : (
                      <>
                        <ArrowRight size={14} />
                        <span>CONTINUE</span>
                      </>
                    )}
                  </button>
                </div>
                {fieldErrors.patientPhone && <p className="text-[10px] font-bold text-red-500 pl-1">⚠ This field is required</p>}
              </div>

              {/* 👥 EXISTING PATIENTS FOUND DROPDOWN (Matches Screenshot 2) */}
              {lookupData && lookupData.patients && lookupData.patients.length > 0 && (
                showPatientDropdown ? (
                  /* 📋 DROPDOWN OPEN: Allows choosing patient or registering new */
                  <div className="mt-5 p-4 sm:p-5 bg-white border border-slate-200/90 rounded-2xl shadow-lg shadow-slate-100/70 space-y-3 animate-fadeIn">
                    <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2 text-xs font-black text-slate-800 uppercase tracking-wider">
                        <Users size={15} className="text-blue-600" />
                        <span>EXISTING PATIENTS FOUND ({lookupData.patients.length})</span>
                      </div>
                      {lookupData.patients.length > 3 && (
                        <span className="text-[10px] font-bold text-slate-400">Scroll to view all</span>
                      )}
                    </div>

                    {/* Quick Filter Search when multiple patients exist */}
                    {lookupData.patients.length > 3 && (
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                          type="text"
                          value={patientFilterQuery}
                          onChange={(e) => setPatientFilterQuery(e.target.value)}
                          placeholder="Search patient name..."
                          className="w-full pl-8 pr-7 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl outline-none font-medium focus:border-blue-500 focus:bg-white transition-all text-slate-800"
                        />
                        {patientFilterQuery && (
                          <button
                            type="button"
                            onClick={() => setPatientFilterQuery('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 cursor-pointer"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )}

                    {/* 📜 SCROLLABLE CONTAINER (Limits height and enables smooth scrolling) */}
                    <div className="space-y-2 max-h-60 sm:max-h-72 overflow-y-auto pr-1.5">
                      {lookupData.patients
                        .filter(p => !patientFilterQuery || p.patientName?.toLowerCase().includes(patientFilterQuery.toLowerCase()))
                        .map((patient, idx) => {
                          const isSelected = selectedPatientProfile && selectedPatientProfile.patientName.toLowerCase() === patient.patientName.toLowerCase();
                          return (
                            <div
                              key={idx}
                              onClick={() => handleSelectPatient(patient)}
                              className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-start justify-between gap-3 ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-50/50 shadow-xs ring-2 ring-blue-600/15'
                                  : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                  isSelected ? 'border-blue-600' : 'border-slate-300'
                                }`}>
                                  {isSelected && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                                </div>
                                <div>
                                  <p className="text-sm font-black text-slate-900 leading-snug">
                                    {patient.patientName}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-slate-500 font-medium">
                                    <span className="flex items-center gap-1">
                                      <Clock size={11} className="text-slate-400" />
                                      {patient.totalRecords || 1} case{patient.totalRecords > 1 ? 's' : ''} on record
                                    </span>
                                    {patient.patientAge && <span>• Age: {patient.patientAge} Yrs</span>}
                                    {patient.patientGender && <span>• {patient.patientGender === 'female' ? 'Female' : 'Male'}</span>}
                                    {patient.bloodGroup && <span>• {patient.bloodGroup}</span>}
                                    {patient.city && <span>• {patient.city}</span>}
                                  </div>
                                </div>
                              </div>
                              <span className={`px-2.5 py-0.5 text-[10px] font-black rounded-lg uppercase tracking-wider shrink-0 ${
                                isSelected ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'
                              }`}>
                                {idx === 0 ? 'Self' : 'Family'}
                              </span>
                            </div>
                          );
                        })}
                    </div>

                    <button
                      type="button"
                      onClick={handleAddNewPatientUnderSamePhone}
                      className={`w-full py-3 px-4 rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-2 text-xs font-black uppercase tracking-wider cursor-pointer ${
                        isNewPatientUnderSamePhone
                          ? 'border-blue-600 bg-blue-50 text-blue-700 font-bold'
                          : 'border-slate-300 hover:border-blue-400 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <UserPlus size={15} />
                      <span>+ REGISTER NEW PATIENT UNDER THIS NUMBER</span>
                    </button>
                  </div>
                ) : (
                  /* 👤 DROPDOWN COLLAPSED / INVISIBLE: Clean compact badge showing current selection */
                  <div className="mt-4 p-3.5 bg-slate-50 border border-slate-200/90 rounded-2xl flex items-center justify-between gap-3 animate-fadeIn">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs shrink-0 ${
                        selectedPatientProfile ? 'bg-blue-600 text-white' : 'bg-indigo-600 text-white'
                      }`}>
                        {selectedPatientProfile ? <Check size={16} /> : <UserPlus size={16} />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-black text-slate-900 truncate">
                            {selectedPatientProfile ? selectedPatientProfile.patientName : 'Registering New Patient'}
                          </span>
                          <span className={`px-2 py-0.5 text-[9px] font-black rounded-md uppercase shrink-0 ${
                            selectedPatientProfile ? 'bg-blue-100 text-blue-800' : 'bg-indigo-100 text-indigo-800'
                          }`}>
                            {selectedPatientProfile ? 'Auto-filled' : 'New User (Empty Form)'}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 font-medium truncate mt-0.5">
                          {selectedPatientProfile
                            ? 'Patient details populated in the form below'
                            : `Enter new patient details below under +91 ${formData.patientPhone}`}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowPatientDropdown(true)}
                      className="px-3 py-1.5 text-xs font-bold text-slate-700 hover:text-blue-700 bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shrink-0 shadow-xs"
                      title="View all patients under this number"
                    >
                      <Users size={13} className="text-blue-600" />
                      <span>Change Patient ({lookupData.patients.length})</span>
                    </button>
                  </div>
                )
              )}

              {/* ✨ BRAND NEW NUMBER CONFIRMATION */}
              {lookupData && lookupData.patients && lookupData.patients.length === 0 && !isSearchingPhone && (
                <div className="mt-4 p-3.5 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between gap-2 text-xs font-bold text-emerald-800 animate-fadeIn">
                  <span className="flex items-center gap-2">
                    <Sparkles size={16} className="text-emerald-600 shrink-0" />
                    <span>✨ New Patient Contact Number: No previous records found for {lookupData.phone}. Form unlocked below for registration.</span>
                  </span>
                </div>
              )}
          </div>
        )}

        {/* 📋 CARD 2: MAIN WORKFLOW CARD */}
        <div className="bg-white rounded-[2rem] sm:rounded-[3rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden min-h-[400px] flex flex-col">

          {/* STEP 1: PATIENT CONSULTATION INFORMATION */}
          {step === 1 && (
            <>
              <div className="p-6 sm:p-8 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-2.5 sm:p-3 rounded-2xl flex-shrink-0 ${intakeCategory === 'appointment' ? 'bg-blue-50 text-[#143E78]' : 'bg-indigo-50 text-indigo-600'}`}>
                    {intakeCategory === 'appointment' ? <CalendarClock className="w-5 h-5 sm:w-6 sm:h-6" /> : <UserPlus className="w-5 h-5 sm:w-6 sm:h-6" />}
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">
                      {intakeCategory === 'appointment' ? 'Patient Consultation Information' : 'Patient Information'}
                    </h2>
                    <p className="text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest mt-0.5">
                      {intakeCategory === 'appointment' ? 'Minimal Appointment Intake' : 'Enter Lead Details'}
                    </p>
                  </div>
                </div>

                {isPhoneConfirmed && (
                  <div className="flex items-center gap-2">
                    {selectedPatientProfile && (
                      <span className="hidden sm:inline-flex px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold items-center gap-1.5">
                        <CheckCircle size={13} /> Auto-filled: {selectedPatientProfile.patientName}
                      </span>
                    )}
                    {isNewPatientUnderSamePhone && (
                      <span className="hidden sm:inline-flex px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-xs font-bold items-center gap-1.5">
                        <UserPlus size={13} /> New Patient Mode
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleResetPhoneLookup}
                      className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-xl flex items-center gap-1.5 transition-all cursor-pointer"
                      title="Change contact number"
                    >
                      <RotateCcw size={12} />
                      <span>Change Number</span>
                    </button>
                  </div>
                )}
              </div>

              {!isPhoneConfirmed ? (
                /* 🔒 LOCKED STATE (MATCHES SCREENSHOT 1) */
                <div className="p-8 sm:p-16 flex flex-col items-center justify-center text-center animate-fadeIn flex-1">
                  <div className="w-16 h-16 rounded-3xl bg-slate-100 text-slate-400 flex items-center justify-center mb-5 shadow-inner">
                    <Lock className="w-8 h-8" />
                  </div>
                  <h4 className="text-lg font-black text-slate-800 mb-2">
                    Patient Consultation Information Locked
                  </h4>
                  <p className="text-sm text-slate-400 font-medium max-w-md leading-relaxed">
                    Enter a mobile number above to search for existing patients or start a new registration.
                  </p>
                </div>
              ) : (
                /* 🔓 UNLOCKED FORM */
                <div className="p-6 sm:p-8 md:p-12 animate-fadeIn flex-1">
                  {/* Dedicated Past Surgery Toggle Button (SURGERY ONLY) */}
                  {intakeCategory === 'surgical' && (
                    <div className="mb-8">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isManualPastEntry: !prev.isManualPastEntry }))}
                        className={`w-full py-3.5 px-4 rounded-2xl font-black text-xs uppercase tracking-wider transition-all border-2 flex items-center justify-between gap-3 ${formData.isManualPastEntry
                          ? 'bg-amber-500 border-amber-500 text-white shadow-lg shadow-amber-100'
                          : 'bg-amber-50/60 border-amber-200 text-amber-800 hover:bg-amber-100/80'
                          }`}
                      >
                        <span className="flex items-center gap-2">
                          <span>⚡</span> Past Completed Surgery (Historical Entry)
                        </span>
                        <span className={`text-[10px] px-2.5 py-1 rounded-lg font-black uppercase ${formData.isManualPastEntry ? 'bg-white/20 text-white' : 'bg-amber-200/60 text-amber-900'
                          }`}>
                          {formData.isManualPastEntry ? 'ENABLED' : 'CLICK TO ENABLE'}
                        </span>
                      </button>

                      {formData.isManualPastEntry && (
                        <div className="mt-3 p-4 bg-amber-50/90 rounded-2xl border border-amber-200 animate-fadeIn">
                          <p className="text-xs font-black text-amber-900">⚡ Historical Entry Mode Enabled</p>
                          <p className="text-[10px] text-amber-700 font-semibold mt-0.5">This lead is a completed past surgery. Customer notifications/emails/OTPs and live payment gateway links will be bypassed.</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    {/* Full Patient Name */}
                    <div className="space-y-1 relative">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                        Full Patient Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        ref={patientNameRef}
                        type="text"
                        name="patientName"
                        value={formData.patientName}
                        onChange={handleInputChange}
                        placeholder="e.g. Rahul Sharma"
                        className={`w-full px-5 py-4 border-2 rounded-2xl outline-none font-bold transition-all ${fieldErrors.patientName
                          ? 'bg-red-50 border-red-400 focus:border-red-500'
                          : selectedPatientProfile
                            ? 'bg-white border-indigo-400 text-slate-900 ring-2 ring-indigo-500/10'
                            : 'bg-slate-50 border-transparent focus:border-indigo-600 focus:bg-white'
                          }`}
                      />
                      {fieldErrors.patientName && <p className="text-[10px] font-bold text-red-500 pl-1">⚠ This field is required</p>}
                    </div>

                    {/* Patient Email */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Patient Email</label>
                      <input
                        type="email"
                        name="patientEmail"
                        value={formData.patientEmail}
                        onChange={handleInputChange}
                        placeholder="patient@example.com"
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-bold transition-all"
                      />
                    </div>

                    {/* Blood Group */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Blood Group</label>
                      <select
                        name="bloodGroup"
                        value={formData.bloodGroup}
                        onChange={handleInputChange}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-bold transition-all"
                      >
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>

                    {/* Date of Birth */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Date of Birth</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-bold transition-all"
                      />
                    </div>

                    {/* Emergency Contact (Surgical) */}
                    {intakeCategory === 'surgical' && (
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Emergency Contact</label>
                        <input
                          type="tel"
                          name="emergencyContact"
                          value={formData.emergencyContact}
                          onChange={handleInputChange}
                          placeholder="10-digit emergency number"
                          maxLength={10}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-mono font-bold transition-all"
                        />
                      </div>
                    )}

                    {/* Patient Age */}
                    <div className="space-y-1 relative">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Patient Age<span className="text-red-500">*</span></label>
                      <input
                        ref={patientAgeRef}
                        type="number"
                        name="patientAge"
                        min={0}
                        max={125}
                        onKeyDown={(e) => {
                          if (['-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        value={formData.patientAge}
                        onChange={handleInputChange}
                        placeholder="Years"
                        className={`w-full px-5 py-4 border-2 rounded-2xl outline-none font-bold transition-all ${fieldErrors.patientAge
                          ? 'bg-red-50 border-red-400 focus:border-red-500'
                          : 'bg-slate-50 border-transparent focus:border-indigo-600 focus:bg-white'
                          }`}
                      />
                      {fieldErrors.patientAge && <p className="text-[10px] font-bold text-red-500 pl-1">⚠ This field is required</p>}
                    </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Patient Gender</label>
                  <select
                    name="patientGender"
                    value={formData.patientGender}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-bold transition-all"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className={`${intakeCategory === 'appointment' ? 'md:col-span-2' : ''} space-y-1 relative`}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Address / City<span className="text-red-500">*</span></label>
                  <input
                    ref={patientAddressRef}
                    type="text"
                    name="patientAddress"
                    value={formData.patientAddress}
                    onChange={handleInputChange}
                    placeholder="City, District, State"
                    className={`w-full px-5 py-4 border-2 rounded-2xl outline-none font-bold transition-all ${fieldErrors.patientAddress
                      ? 'bg-red-50 border-red-400 focus:border-red-500'
                      : 'bg-slate-50 border-transparent focus:border-indigo-600 focus:bg-white'
                      }`}
                  />
                  {fieldErrors.patientAddress && <p className="text-[10px] font-bold text-red-500 pl-1">⚠ This field is required</p>}
                </div>

                {intakeCategory === 'surgical' && (
                  <div className="md:col-span-2 space-y-1 relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Treatment / Procedure Required<span className="text-red-500">*</span></label>
                    <input
                      ref={treatmentRequiredRef}
                      type="text"
                      name="treatmentRequired"
                      value={formData.treatmentRequired}
                      onChange={handleInputChange}
                      placeholder="e.g. Laparoscopic Cholecystectomy"
                      className={`w-full px-5 py-4 border-2 rounded-2xl outline-none font-bold transition-all ${fieldErrors.treatmentRequired
                        ? 'bg-red-50 border-red-400 focus:border-red-500'
                        : 'bg-slate-50 border-transparent focus:border-indigo-600 focus:bg-white'
                        }`}
                    />
                    {fieldErrors.treatmentRequired && <p className="text-[10px] font-bold text-red-500 pl-1">⚠ This field is required</p>}
                  </div>
                )}

                {/* Chief Complaints - Required for Appointment, Optional for Surgical */}
                <div className="md:col-span-2 space-y-1 relative">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                    Chief Complaints / Symptoms {intakeCategory === 'appointment' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    ref={chiefComplaintsRef}
                    name="chiefComplaints"
                    value={formData.chiefComplaints}
                    onChange={handleInputChange}
                    rows={intakeCategory === 'appointment' ? "3" : "2"}
                    placeholder={intakeCategory === 'appointment' ? "e.g. High fever for 3 days, acute headache, joint pain, general physician consultation" : "Primary symptoms or complaints reported by the patient"}
                    className={`w-full px-5 py-4 border-2 rounded-2xl outline-none font-bold transition-all resize-none ${fieldErrors.chiefComplaints
                        ? 'bg-red-50 border-red-400 focus:border-red-500'
                        : 'bg-slate-50 border-transparent focus:border-indigo-600 focus:bg-white'
                      }`}
                  ></textarea>
                  {fieldErrors.chiefComplaints && <p className="text-[10px] font-bold text-red-500 pl-1">⚠ Please enter chief complaints or symptoms</p>}
                </div>

                {intakeCategory === 'appointment' && (
                  <div className="md:col-span-2 space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Specialty / Treatment Needed (Optional)</label>
                    <input
                      type="text"
                      name="treatmentRequired"
                      value={formData.treatmentRequired}
                      onChange={handleInputChange}
                      placeholder="e.g. General Medicine, Orthopedic Consult, Dermatology"
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-bold transition-all"
                    />
                  </div>
                )}

                <div className="md:col-span-2 space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Comorbidities (Any Other Chronic Illness)</label>
                  <textarea
                    name="comorbidities"
                    value={formData.comorbidities}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="e.g. BP, Diabetes, Asthma, Thyroid, Heart Disease"
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-bold transition-all resize-none"
                  ></textarea>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Past Medical History</label>
                  <textarea
                    name="pastMedicalHistory"
                    value={formData.pastMedicalHistory}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Relevant past medical history, previous surgeries, or long-term conditions"
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl outline-none font-bold transition-all resize-none"
                  ></textarea>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

          {/* STEP 2: ROLE BASED CHOICE (DOCTOR & HOSPITAL ONLY) */}
          {step === 2 && (user?.role === 'doctor' || user?.role === 'hospital') && (
            <div className="p-6 sm:p-8 md:p-12 animate-fadeIn flex-1">
              <div className="flex items-center gap-4 mb-6 sm:mb-10 pb-6 border-b border-slate-50">
                <div className="p-2 sm:p-3 bg-indigo-50 rounded-xl sm:rounded-2xl flex-shrink-0">
                  <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">Intent & Facility matching</h2>
                  <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Operational Role Strategy</p>
                </div>
              </div>

              <div className="space-y-12">
                {/* DOCTOR SPECIFIC: HOSPITAL PICKER */}
                {(user?.role === 'doctor' || user?.role === 'admin') && (
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Operating Facility (Where surgery will happen?)</label>
                    <SearchableSelect
                      options={hospitals}
                      value={formData.assignedHospital}
                      onChange={(id) => setFormData({ ...formData, assignedHospital: id })}
                      label="Select In-panel Hospital"
                      type="hospital"
                      placeholder="Search facility name..."
                    />
                  </div>
                )}

                {/* HOSPITAL SPECIFIC: AUTO INFO */}
                {user?.role === 'hospital' && (
                  <div className="p-6 bg-emerald-50 rounded-3xl border border-emerald-100 flex items-center gap-4">
                    <div className="p-3 bg-white rounded-2xl shadow-sm">
                      <Building className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Facility Locked</p>
                      <p className="text-base font-black text-slate-800 uppercase">{partnerProfile?.hospitalName || 'Your Hospital'}</p>
                    </div>
                  </div>
                )}

                {/* INTENT CHOICE: ONLY LEAD VS SURGERY */}
                {user?.role !== 'marketing_agent' ? (
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4 ml-1">Strategy & Intent Alignment</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[
                        {
                          id: 'lead_only',
                          label: user?.role === 'hospital' ? 'Only Providing Lead' : 'Referring - Only Lead',
                          desc: user?.role === 'hospital' ? 'We are referring this patient. DOXEZ should manage the full clinical journey elsewhere.' : 'I am sharing this case. DOXEZ handles the treatment and facility.',
                          icon: Activity
                        },
                        {
                          id: 'surgery',
                          label: user?.role === 'doctor' ? 'I am Performing Surgery' : 'Operating in our Hospital',
                          desc: user?.role === 'doctor' ? 'I will be the primary operating surgeon at the selected facility.' : 'The procedure will be carried out within our clinical infrastructure.',
                          icon: Stethoscope
                        }
                      ].map((type) => (
                        <button
                          key={type.id}
                          onClick={() => setFormData({ ...formData, leadType: type.id })}
                          className={`p-4 sm:p-6 rounded-2xl sm:rounded-3xl border-2 transition-all flex items-start gap-3 sm:gap-4 text-left ${formData.leadType === type.id
                            ? 'border-indigo-600 bg-indigo-50 ring-4 ring-indigo-50 shadow-xl'
                            : 'border-slate-100 bg-white hover:border-slate-200'
                            }`}
                        >
                          <div className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl flex-shrink-0 ${formData.leadType === type.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                            <type.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-black uppercase tracking-[0.1em] text-[10px] sm:text-[11px] mb-1 sm:mb-1.5 ${formData.leadType === type.id ? 'text-indigo-700' : 'text-slate-800'}`}>{type.label}</p>
                            <p className="text-[10px] sm:text-[11px] font-bold text-slate-400 leading-relaxed">{type.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-5 sm:p-8 bg-blue-50 rounded-[2rem] sm:rounded-[2.5rem] border border-blue-100 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                      <ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-black text-blue-800 text-lg sm:text-xl tracking-tight">Standard Referral Lead</h4>
                      <p className="text-[10px] sm:text-xs font-bold text-blue-400 uppercase tracking-tighter mt-1">Lead submission initiated by Marketing Partner</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: ELIGIBILITY & REPORTS */}
          {step === 3 && (
            <div className="p-6 sm:p-8 md:p-12 animate-fadeIn flex-1 space-y-8 sm:space-y-12">
              <div className="flex items-center gap-4 mb-4 pb-6 border-b border-slate-50">
                <div className="p-2 sm:p-3 bg-emerald-50 rounded-xl sm:rounded-2xl flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">Medical & Eligibility</h2>
                  <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Upload Documents</p>
                </div>
              </div>

              {/* Mode Selection */}
              <div ref={eligibilityModeRef} className="relative">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-6 text-center">Primary Coverage Mode</label>
                <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 rounded-2xl transition-all ${fieldErrors.eligibilityMode ? 'ring-2 ring-red-400 ring-offset-2 p-2' : ''}`}>
                  {[
                    { id: 'ayushman', label: 'Ayushman', icon: CreditCard },
                    { id: 'cash', label: 'Cash', icon: Activity },
                    { id: 'insurance', label: 'Insurance', icon: CheckCircle }
                  ].map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setFormData({ ...formData, eligibilityMode: m.id }); setFieldErrors(prev => ({ ...prev, eligibilityMode: false })); }}
                      className={`flex items-center justify-center gap-2 p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 font-black uppercase text-[9px] sm:text-[10px] tracking-widest transition-all ${formData.eligibilityMode === m.id ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl' : 'border-slate-100 bg-slate-50 text-slate-400'
                        }`}
                    >
                      <m.icon className="w-4 h-4" /> {m.label}
                    </button>
                  ))}
                </div>
              </div>


              {/* 🆔 SECTION 1: IDENTITY PROOF */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-xs">1</div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Identification Protocol</label>
                </div>

                <div className="p-6 sm:p-8 bg-slate-50 rounded-[2rem] sm:rounded-[2.5rem] border border-slate-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                    {/* Ayushman Card slotted specifically if active */}
                    {formData.eligibilityMode === 'ayushman' && (
                      <div className="space-y-3 sm:space-y-4">
                        <p className="text-xs sm:text-sm font-black text-slate-700">1.1 Ayushman Card</p>
                        <div className="border-2 border-dashed border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex flex-col items-center justify-center bg-white relative min-h-[140px] sm:min-h-[160px] transition-all hover:border-indigo-200">
                          {formData.ayushmanCardImage ? (
                            <div className="w-full h-full relative group">
                              <img src={formData.ayushmanCardImage} className="w-full h-28 sm:h-32 object-cover rounded-xl sm:rounded-2xl shadow-lg border border-slate-100" alt="Ayushman Card" />
                              <div className="absolute -top-2 -right-2 flex gap-1">
                                <a
                                  href={formData.ayushmanCardImage}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 sm:p-2 bg-indigo-600 text-white rounded-full shadow-xl hover:scale-110 transition-all"
                                >
                                  <Eye className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                                </a>
                                <button
                                  onClick={() => setFormData({ ...formData, ayushmanCardImage: null })}
                                  className="p-1.5 sm:p-2 bg-red-500 text-white rounded-full shadow-xl hover:scale-110 transition-all"
                                >
                                  <XCircle className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <input type="file" id="ayush-up" className="hidden" onChange={(e) => handleFileUpload(Array.from(e.target.files), 'ayushman')} accept="image/*" />
                              <label htmlFor="ayush-up" className="flex flex-col items-center cursor-pointer group text-center">
                                <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-slate-300 mb-2 group-hover:text-indigo-400 transition-all" />
                                <p className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-indigo-600 transition-all">Upload Card Image</p>
                              </label>
                            </>
                          )}
                        </div>
                        {/* ABHA ID inside Ayushman section */}
                        <div className="space-y-1 pt-1">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">ABHA ID <span className="text-slate-300 font-semibold normal-case">(Optional)</span></label>
                          <input
                            type="tel"
                            inputMode="numeric"
                            name="abhaId"
                            value={formData.abhaId}
                            onChange={handleInputChange}
                            placeholder="Enter 14-digit ABHA number"
                            maxLength={14}
                            className="w-full px-4 py-3 bg-white border-2 border-slate-200 focus:border-indigo-500 rounded-2xl outline-none font-mono font-bold text-sm transition-all"
                          />
                          {formData.abhaId.length > 0 && formData.abhaId.length < 14 && (
                            <p className="text-[10px] font-bold text-amber-500 pl-1">⚠ Must be 14 digits ({formData.abhaId.length}/14)</p>
                          )}
                          {formData.abhaId.length === 14 && (
                            <p className="text-[10px] font-bold text-emerald-500 pl-1">✓ Valid ABHA ID</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Standard Identification Proof */}
                    <div className="space-y-3 sm:space-y-4">
                      <p className="text-xs sm:text-sm font-black text-slate-700">{formData.eligibilityMode === 'ayushman' ? '1.2 ID Proof' : 'Patient ID Proof'}</p>
                      <div className="border-2 border-dashed border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex flex-col items-center justify-center bg-white relative min-h-[140px] sm:min-h-[160px] transition-all hover:border-indigo-200">
                        {formData.patientIdProof ? (
                          <div className="w-full h-full relative group">
                            <img src={formData.patientIdProof} className="w-full h-28 sm:h-32 object-cover rounded-xl sm:rounded-2xl shadow-lg border border-slate-100" alt="ID Proof" />
                            <div className="absolute -top-2 -right-2 flex gap-1">
                              <a
                                href={formData.patientIdProof}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 sm:p-2 bg-indigo-600 text-white rounded-full shadow-xl hover:scale-110 transition-all"
                              >
                                <Eye className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                              </a>
                              <button
                                onClick={() => setFormData({ ...formData, patientIdProof: null })}
                                className="p-1.5 sm:p-2 bg-red-500 text-white rounded-full shadow-xl hover:scale-110 transition-all"
                              >
                                <XCircle className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <input type="file" id="id-up" className="hidden" onChange={(e) => handleFileUpload(Array.from(e.target.files), 'id_proof')} accept="image/*" />
                            <label htmlFor="id-up" className="flex flex-col items-center cursor-pointer group text-center">
                              <ShieldCheck className="w-6 h-6 sm:w-8 sm:h-8 text-slate-300 mb-2 group-hover:text-indigo-400 transition-all" />
                              <p className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-indigo-600 transition-all"> {formData.eligibilityMode === 'ayushman' ? 'Upload Aadhaar/PAN' : 'Upload ID Proof'} </p>
                            </label>
                          </>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* 🏥 SECTION 2: MEDICAL INVESTIGATIONS */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-xs">2</div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Patient Reports</label>
                </div>

                <div className="p-6 sm:p-8 bg-slate-50 rounded-[2.5rem] sm:rounded-[3rem] border border-slate-100">
                  <div className="flex flex-col gap-6 sm:gap-8">
                    <div className="w-full">
                      <p className="text-xs sm:text-sm font-black text-slate-700 mb-1">Medical Documents</p>
                      <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase leading-relaxed mb-4">Upload clinical findings, surgery readiness reports, or past prescriptions.</p>

                      <input type="file" multiple id="rep-up" className="hidden" onChange={(e) => handleFileUpload(Array.from(e.target.files), 'medical')} />
                      <label htmlFor="rep-up" className="w-full flex items-center justify-center gap-2 px-6 py-3.5 sm:py-4 bg-indigo-600 text-white rounded-xl sm:rounded-2xl font-black text-[9px] sm:text-[10px] uppercase shadow-xl shadow-indigo-100 cursor-pointer hover:bg-indigo-700 transition-all">
                        <Upload className="w-4 h-4" /> Add Medical reports
                      </label>
                    </div>

                    <div className="w-full">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {formData.medicalReports.map((r, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 sm:p-4 bg-white border border-slate-100 rounded-xl sm:rounded-2xl shadow-sm animate-fadeIn group">
                            <div className="w-8 h-8 rounded-lg sm:rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                              <FileText className="w-3.5 h-3.5 sm:w-4 h-4" />
                            </div>
                            <span className="flex-1 text-[10px] sm:text-xs font-bold text-slate-600 truncate">{r.name}</span>
                            <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all">
                              <a
                                href={r.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 sm:p-2 text-slate-400 hover:text-indigo-600 transition-all"
                                title="Review Document"
                              >
                                <Eye className="w-3.5 h-3.5 sm:w-4 h-4" />
                              </a>
                              <button
                                onClick={() => {
                                  const up = [...formData.medicalReports];
                                  up.splice(i, 1);
                                  setFormData({ ...formData, medicalReports: up });
                                }}
                                className="p-1.5 sm:p-2 text-slate-400 hover:text-red-500 transition-all"
                                title="Remove"
                              >
                                <XCircle className="w-3.5 h-3.5 sm:w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {formData.medicalReports.length === 0 && (
                          <div className="w-full min-h-[120px] sm:min-h-[140px] flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center bg-white/50">
                            <ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-slate-200 mb-2" />
                            <p className="text-[8px] sm:text-[9px] font-black text-slate-300 uppercase tracking-widest">Clinical Findings Missing</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 🔘 NAVIGATION BAR */}
          {(step > 1 || isPhoneConfirmed) && (
            <div className="bg-slate-50/50 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 gap-6">
              {intakeCategory === 'appointment' ? (
                <>
                  <div className="text-xs font-bold text-slate-500 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>Minimal Outpatient Intake • Direct Consultation Lead</span>
                  </div>

                  <button
                    type="button"
                    onClick={handleAppointmentSubmit}
                    disabled={loading}
                    className="w-full sm:w-auto px-8 sm:px-12 py-3.5 sm:py-4 rounded-[1.5rem] sm:rounded-[2rem] font-black text-white text-xs sm:text-sm uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-3 bg-[#143E78] hover:bg-[#0f2d57] shadow-blue-950/20 cursor-pointer disabled:opacity-50"
                  >
                    {loading ? 'Creating Appointment Lead...' : 'Create Appointment Lead'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  {step > 1 ? (
                    <button
                      onClick={() => setStep((user?.role !== 'doctor' && user?.role !== 'hospital') && step === 3 ? 1 : step - 1)}
                      className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-all w-full sm:w-auto justify-center sm:justify-start cursor-pointer"
                    >
                      <ArrowLeft className="w-4 h-4" /> Go Back
                    </button>
                  ) : <div className="hidden sm:block" />}

                  <button
                    onClick={step === 3 ? handleSubmit : nextStep}
                    disabled={loading}
                    className={`w-full sm:w-auto px-8 sm:px-12 py-3.5 sm:py-4 rounded-[1.5rem] sm:rounded-[2rem] font-black text-white text-xs sm:text-sm uppercase tracking-widest shadow-xl transition-all flex items-center justify-center gap-3 cursor-pointer ${step === 3 ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-100' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100'
                      }`}
                  >
                    {loading ? 'Saving Lead...' : step === 3 ? 'Finalize Registration' : 'Save & Continue'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          )}

        </div>

        {/* Audit Disclaimer */}
        <p className="text-center mt-8 text-slate-400 font-bold text-[9px] uppercase tracking-[0.2em]">
          DOXEZ CORE DEAL SYSTEM • ENCRYPTED SESSION • ROLE: {user?.role.replace('_', ' ')}
        </p>
      </div>
    </div>
  );
};

export default LeadCreate;